/* eslint-disable react-hooks/exhaustive-deps */
import { useQueryClient } from "@tanstack/react-query";
import i18n from "i18next";

import { JSX, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { UpdateProfileForm } from "@/pages/gate/UpdateProfilePage.tsx";

import Button from "@/components/buttons/Button.tsx";
import AdditionalInfo from "@/components/gate/update-profile/AdditionalInfo.tsx";
import EditProfileForm from "@/components/gate/update-profile/EditProfileForm.tsx";
import { UpdateProfileProps, UpdateProfileTab } from "@/components/gate/update-profile/types.ts";
import { IconEmptyUser, IconPencil } from "@/components/icons";
import UpdateProfileLayout from "@/components/layout/UpdateProfileLayout.tsx";
import Modal from "@/components/staff/modals/Modal.tsx";

import { PatchProfile } from "@/api/user/user.types";
import { ROUTES } from "@/config/routes.ts";
import { LOGIN_KEY } from "@/hooks/api/login.hooks";
import { USER_KEYS, usePatchProfile } from "@/hooks/api/user.hooks.ts";
import useBeforeUnload from "@/hooks/useBeforeUnload.ts";

import DeleteProfileModal from "../modals/DeleteProfileModal";

const UpdateFormTab = ({ profile, setTab }: UpdateProfileProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBeforeUnload(true);

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
  } = useFormContext<UpdateProfileForm>();
  const { mutate } = usePatchProfile();
  const [showDeleteModal, setDeleteModal] = useState(false);
  const queryClient = useQueryClient();

  const picture = getValues("picture");

  const getGradient = useMemo(() => {
    if (!picture || !picture.color) {
      return "linear-gradient(rgba(255, 75, 0, 0.6) 0%, rgba(255, 75, 0, 0.25) 100%)";
    }
    const bigint = parseInt(picture.color.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `linear-gradient(rgba(${r}, ${g}, ${b}, 0.6) 0%, rgba(${r}, ${g}, ${b}, 0.25) 100%)`;
  }, []);

  const pushBack = (): void => {
    const navigateToProfiles = (): void => navigate(ROUTES.GATE.PROFILES, { state: { isKids: profile?.is_kids } });

    if (isDirty) {
      window.confirm(t("General.back-warning")) && navigateToProfiles();
    } else {
      navigateToProfiles();
    }
  };

  const onSubmit = handleSubmit((data) => {
    const picture_id = data.picture?.id ?? null;

    const payload = profile.is_kids ? { name: data.name, picture_id } : { ...data, picture_id };

    mutate(
      { user_id: profile.id, data: payload as PatchProfile },
      {
        onSuccess: () => {
          navigate(ROUTES.GATE.PROFILES);
          queryClient.invalidateQueries({ queryKey: LOGIN_KEY.profileList });
          queryClient.invalidateQueries({ queryKey: USER_KEYS.profileDetail(profile.id) });
          if (data.interface_lang) {
            i18n.changeLanguage(data.interface_lang);
          }
        },
      },
    );
  });

  const closeModal = () => {
    setDeleteModal(false);
  };
  const showModal = () => {
    setDeleteModal(true);
  };

  return (
    <UpdateProfileLayout title={t("Profile.edit")} onBackClick={pushBack}>
      <div>
        <hr className={"mb-8 mt-4 h-px w-full border-surface-container"} />
        <div className={"flex items-start gap-10 max-md:flex-col max-md:items-center max-md:gap-6"}>
          <div className={"relative rounded-full bg-black-1"}>
            <div
              className={"overflow-hidden rounded-full"}
              style={{
                background: getGradient,
              }}
            >
              {picture?.resized ? (
                <img
                  src={picture?.resized}
                  alt={"profile-image"}
                  className={"size-40 translate-y-[1.5rem] scale-110 transform object-contain max-sm:size-[6.25rem]"}
                />
              ) : (
                <>
                  <IconEmptyUser className="size-40" />
                </>
              )}
              <button
                type={"button"}
                title={t("Actions.edit")}
                onClick={() => setTab(UpdateProfileTab.PICTURES)}
                className={"absolute bottom-0 right-3 rounded-full bg-surface-container p-2 max-sm:right-0"}
              >
                <IconPencil variant={1} className={"size-4 stroke-on-primary"} />
              </button>
            </div>
          </div>
          <form onSubmit={onSubmit} className={"w-[27.5rem] max-md:w-full"}>
            <EditProfileForm profile={profile} />

            {!profile.is_kids && <AdditionalInfo setTab={setTab} />}
            <div className={"mt-8 grid grid-cols-2 gap-6"}>
              {profile.is_kids || profile.is_adult ? (
                <div></div>
              ) : (
                <Button
                  type={"button"}
                  aria-label={t("DeleteProfile.title")}
                  size="medium"
                  color="black"
                  onClick={showModal}
                >
                  {t("DeleteProfile.title")}
                </Button>
              )}
              <Button type={"submit"} aria-label={t("Actions.save")} size="medium">
                {t("Actions.save")}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <Modal isOpen={showDeleteModal} closeModal={closeModal} className="max-w-[30.5rem] bg-surface-bright">
        <DeleteProfileModal closeModal={closeModal} profileId={profile.id} />
      </Modal>
    </UpdateProfileLayout>
  );
};

export default UpdateFormTab;
